/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Fullscreen image lightbox — opened when a sent/received image tile is
// tapped (the host drives `open` + `imageUri` via the image variant's
// `onTapOpen`). Dark scrim, centered image, close affordance, and an
// optional download control (web-only — rendered via a native `<a download>`
// so the browser saves the asset; native platforms hide it).
//
// Cross-platform: built on RN `Modal` + `Image` + `Pressable` so it works
// on web (RN-Web), macOS, iOS, and Android. The scrim press closes; the
// image itself swallows the press so tapping the photo doesn't dismiss.

import React from "react";
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Download, X } from "lucide-react-native";
import { WebPortal } from "./composer/WebPortal";

const IS_WEB =
  typeof document !== "undefined" && typeof window !== "undefined";

export interface ImageLightboxProps {
  readonly open: boolean;
  readonly imageUri?: string | undefined;
  readonly onClose: () => void;
}

export const ImageLightbox = React.memo<ImageLightboxProps>(
  ({ open, imageUri, onClose }) => {
    const { width: winW, height: winH } = useWindowDimensions();
    // Track load lifecycle so a broken/expired URL shows an error instead of
    // a blank frame, and a slow decrypt shows a spinner instead of nothing.
    const [status, setStatus] = React.useState<"loading" | "loaded" | "error">(
      "loading",
    );
    React.useEffect(() => {
      setStatus("loading");
    }, [imageUri]);
    if (!open) return null;

    const hasImage = imageUri !== undefined && imageUri.length > 0;

    const scrim = (
        /* Dark scrim — tap anywhere to dismiss. */
        <Pressable
          accessibilityRole="none"
          onPress={onClose}
          className="flex-1 items-center justify-center bg-black/90"
        >
          {/* Centered image. Swallow the press so tapping the photo
              itself doesn't bubble up to the scrim and close. */}
          {hasImage ? (
            <Pressable
              onPress={(e) => e.stopPropagation?.()}
              className="max-w-full max-h-full items-center justify-center"
            >
              {status === "error" ? (
                <View
                  className="items-center justify-center"
                  style={{ width: winW, height: winH }}
                >
                  <Text className="font-sans text-[15px] leading-[20px] text-white/70">
                    Couldn't load image
                  </Text>
                </View>
              ) : (
                <Image
                  source={{ uri: imageUri }}
                  resizeMode="contain"
                  accessibilityIgnoresInvertColors
                  onLoad={() => setStatus("loaded")}
                  onError={() => setStatus("error")}
                  // NativeWind `w-screen h-screen` on a react-native-web
                  // <Image> collapses to 0×0 — size to the viewport with
                  // explicit numeric style (documented fallback). contain
                  // keeps the photo centered + aspect-correct.
                  style={{ width: winW, height: winH }}
                />
              )}
              {status === "loading" ? (
                <View
                  pointerEvents="none"
                  className="absolute inset-0 items-center justify-center"
                >
                  <ActivityIndicator size="large" color="#FFFFFF" />
                </View>
              ) : null}
            </Pressable>
          ) : null}

          {/* Top-right controls — close + optional web download. */}
          <View
            pointerEvents="box-none"
            className="absolute top-0 right-0 flex-row items-center gap-2 p-4"
          >
            {IS_WEB && hasImage ? (
              <WebDownloadButton uri={imageUri as string} />
            ) : null}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={onClose}
              hitSlop={8}
              className="w-9 h-9 rounded-full items-center justify-center bg-white/10"
            >
              <X size={20} strokeWidth={2} color="#FFFFFF" />
            </Pressable>
          </View>
        </Pressable>
    );

    // Web: body portal → instant paint (skip RN-Web Modal's mount gate).
    if (IS_WEB) {
      return (
        <WebPortal>
          <View style={StyleSheet.absoluteFill}>{scrim}</View>
        </WebPortal>
      );
    }
    return (
      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={onClose}
        accessibilityViewIsModal
      >
        {scrim}
      </Modal>
    );
  },
);

ImageLightbox.displayName = "ImageLightbox";

// Whether the browser's native `<a download>` will actually save the asset.
// The `download` attribute is IGNORED for cross-origin URLs (the browser
// navigates instead of saving), so it's only trustworthy for same-origin,
// `blob:` and `data:` URLs.
function nativeDownloadWorks(uri: string): boolean {
  if (uri.startsWith("blob:") || uri.startsWith("data:")) return true;
  try {
    return new URL(uri, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

// Web-only download affordance. Rendered as a real `<a download>` so the
// browser writes the asset to disk instead of navigating. For cross-origin
// URLs (where `download` is ignored) we intercept the click and fetch →
// blob → object-URL → download instead, falling back to opening a new tab.
function WebDownloadButton({ uri }: { uri: string }): JSX.Element {
  const onClick = (e: { preventDefault: () => void }): void => {
    if (nativeDownloadWorks(uri)) return; // let the <a download> save it
    e.preventDefault();
    void (async () => {
      try {
        const res = await fetch(uri);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch {
        // Best-effort fallback: at least surface the asset rather than
        // silently doing nothing.
        window.open(uri, "_blank", "noopener");
      }
    })();
  };
  return React.createElement(
    "a",
    {
      href: uri,
      download: "",
      onClick,
      "aria-label": "Download image",
      style: {
        width: 36,
        height: 36,
        borderRadius: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.10)",
        textDecoration: "none",
      },
    },
    React.createElement(Download, { size: 20, strokeWidth: 2, color: "#FFFFFF" }),
  );
}
